"use client";

import * as React from "react";
import { createPortal } from "react-dom";
import BigNumber from "bignumber.js";
import type { Environment } from "../../../types";
import { resolveEnvironment } from "../../../lib/environment";
import { cn } from "../../../lib/utils";
import { TrackingBoundary } from "../../../lib/sentry";
import {
  resolveEnvValue,
  useRemoteConfigValue,
  type AppsConfig,
} from "../../../lib/remote-config";
import { BackIcon, ChevronDownIcon, CloseIcon } from "../../wallet-info/components/icons";
import {
  ArrowDownIcon,
  ArrowRightIcon,
  ArrowRightLeftIcon,
  HistoryIcon,
  SearchIcon,
  SlidersIcon,
} from "./icons";
import {
  useBridgeFlow,
  type BridgeFlow as BridgeFlowState,
} from "../model/useBridgeFlow";
import type {
  BridgeApprovalInfo,
  BridgeApproveFn,
  BridgeFailedInfo,
  BridgeGetApprovalFn,
  BridgeGetToTokensFn,
  BridgeHistoryItem,
  BridgeInfoRow,
  BridgeInfoTokenRef,
  BridgePathType,
  BridgeQuoteFn,
  BridgeSubmittedInfo,
  BridgeSubmitFn,
  BridgeToken,
  BridgeTxSummary,
} from "../types";
import "../styles/bridge-flow.css";

export interface BridgeFlowProps {
  walletAddress: string;
  tokens: BridgeToken[];
  initialFromToken?: BridgeToken;
  initialToToken?: BridgeToken;
  history?: BridgeHistoryItem[];
  initialSlippage?: string;
  termsUrl?: string;
  env?: Environment;
  getQuote?: BridgeQuoteFn;
  getToTokens?: BridgeGetToTokensFn;
  getApproval?: BridgeGetApprovalFn;
  approveBridge?: BridgeApproveFn;
  submitBridge?: BridgeSubmitFn;
  onSubmitted?: (info: BridgeSubmittedInfo) => void;
  onSuccess?: (info: BridgeSubmittedInfo) => void;
  onFailed?: (info: BridgeFailedInfo) => void;
  onClose?: () => void;
  onBackToWallet?: () => void;
  className?: string;
}

type TokenPickerSide = "from" | "to";
type HistoryFilter = "all" | "completed" | "pending";

const ThemeContext = React.createContext<string | undefined>(undefined);

function useThemeAttr(): string | undefined {
  return React.useContext(ThemeContext);
}

function useDetectTheme(ref: React.RefObject<HTMLElement | null>): string | undefined {
  const [theme, setTheme] = React.useState<string | undefined>(undefined);
  React.useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const read = () => {
      const closest = el.closest("[data-theme]");
      setTheme(closest?.getAttribute("data-theme") ?? undefined);
    };
    read();
    const observer = new MutationObserver(read);
    observer.observe(document.documentElement, { attributes: true, subtree: true, attributeFilter: ["data-theme"] });
    return () => observer.disconnect();
  }, [ref]);
  return theme;
}

const SLIPPAGE_PRESETS = [
  { value: "0.1", label: "0.1%" },
  { value: "0.5", label: "0.5%" },
  { value: "1", label: "1%" },
  { value: "5", label: "5%" },
] as const;

const HISTORY_FILTERS: Array<{ value: HistoryFilter; label: string }> = [
  { value: "all", label: "ALL" },
  { value: "completed", label: "Completed" },
  { value: "pending", label: "Pending" },
];

function formatSlippageLabel(value: string, effective?: string): string {
  // In Auto mode show the resolved percent alongside the label (e.g. "Auto 1%"),
  // matching crossd's chip.
  if (value === "auto") return effective ? `Auto ${effective}%` : "Auto";
  return value ? `${value}%` : "1%";
}

function sanitizeSlippageInput(value: string): string {
  const cleaned = value.replace(/[^\d.]/g, "");
  const [integer = "", ...decimalParts] = cleaned.split(".");
  const decimal = decimalParts.join("");
  if (decimalParts.length === 0) return integer;
  return `${integer}.${decimal.slice(0, 2)}`;
}

function tokenKey(token: BridgeToken): string {
  return `${token.chainId}:${token.address.toLowerCase()}`;
}

function formatBalance(value: string): string {
  const n = Number(value);
  if (!Number.isFinite(n)) return value || "0";
  return n.toLocaleString("en-US", {
    maximumFractionDigits: n >= 1 ? 2 : 6,
  });
}

function formatUsd(amount: string, token?: BridgeToken): string {
  const n = Number(amount || "0");
  const price = token?.priceUsd ?? 0;
  const value = Number.isFinite(n) ? n * price : 0;
  return `≈ $${value.toLocaleString("en-US", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })} USD`;
}

// Truncate (floor) a decimal string to at most `maxDp` fraction digits for
// display, trimming trailing zeros. Display-only — never reused in computation.
function clampDecimals(value: string, maxDp = 6): string {
  if (!value) return value;
  const [intPart, fracPart = ""] = value.split(".");
  if (!fracPart) return intPart;
  const truncated = fracPart.slice(0, maxDp).replace(/0+$/, "");
  return truncated ? `${intPart}.${truncated}` : intPart;
}

function exceedsBalance(amount: string, token?: BridgeToken): boolean {
  if (!token) return false;
  const value = new BigNumber(amount || "0");
  const balance = new BigNumber(token.balance || "0");
  if (!value.isFinite() || !balance.isFinite()) return false;
  return value.isGreaterThan(balance);
}

function formatBalanceUsd(token: BridgeToken): string | null {
  if (token.priceUsd === undefined) return null;
  const balance = new BigNumber(token.balance || "0");
  if (!balance.isFinite()) return null;
  return `≈ $${balance.multipliedBy(token.priceUsd).toFormat(2)} USD`;
}

function formatTokenPriceUsd(token: BridgeToken): string | null {
  if (token.priceUsd === undefined) return null;
  const price = new BigNumber(token.priceUsd);
  if (!price.isFinite()) return null;
  return `$${price.toFormat(price.isLessThan(1) ? 4 : 2)}`;
}

function formatRateDecimal(value: BigNumber): string {
  if (!value.isFinite() || value.isNaN()) return "0";
  const fixed = value.decimalPlaces(6, BigNumber.ROUND_DOWN).toFixed(6);
  const trimmed = fixed.replace(/\.?0+$/, "");
  const [integer = "0", decimal] = trimmed.split(".");
  const integerWithCommas = new BigNumber(integer || "0").toFormat(0);
  return decimal ? `${integerWithCommas}.${decimal}` : integerWithCommas;
}

function shortHash(hash: string): string {
  if (hash.length <= 14) return hash;
  return `${hash.slice(0, 6)}...${hash.slice(-6)}`;
}

function formatHistoryTimestamp(timestamp: string): string {
  const date = new Date(timestamp);
  if (Number.isNaN(date.getTime())) return timestamp;

  const pad = (value: number) => value.toString().padStart(2, "0");
  return [
    `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`,
    `${pad(date.getHours())}:${pad(date.getMinutes())}:${pad(date.getSeconds())}`,
  ].join(" ");
}

function formatHistoryTokenAmount(value: string): string {
  const amount = new BigNumber(value || "0");
  if (!amount.isFinite() || amount.isNaN()) return value || "0";
  return formatRateDecimal(amount);
}

function formatHistoryUsd(amount: string, token?: BridgeToken): string | null {
  if (token?.priceUsd === undefined) return null;
  const value = new BigNumber(amount || "0").multipliedBy(token.priceUsd);
  if (!value.isFinite() || value.isNaN()) return null;
  return `$${value.toFormat(2)}`;
}

function getHistoryStatus(
  item: BridgeHistoryItem,
): "pending" | "completed" | "failed" {
  if (item.status === "success") return "completed";
  if (item.status === "failed") return "failed";
  return "pending";
}

function historyMatchesFilter(item: BridgeHistoryItem, filter: HistoryFilter): boolean {
  if (filter === "all") return true;
  return getHistoryStatus(item) === filter;
}

function historyStatusLabel(status: ReturnType<typeof getHistoryStatus>): string {
  if (status === "completed") return "Completed";
  if (status === "failed") return "Failed";
  return "Pending";
}

function chainLabel(chainId: number): string {
  if (chainId === 56 || chainId === 97) return "BSC";
  if (chainId === 612044 || chainId === 5555) return "CROSS";
  return `Chain ${chainId}`;
}

function sameBridgeAsset(a?: BridgeToken, b?: BridgeToken): boolean {
  return (
    !!a &&
    !!b &&
    a.chainId === b.chainId &&
    a.address.toLowerCase() === b.address.toLowerCase()
  );
}

function inferPathType(
  from?: BridgeToken,
  to?: BridgeToken,
): BridgePathType | undefined {
  if (!from || !to) return undefined;
  if (from.chainId === to.chainId) return "swap";
  if (
    sameBridgeAsset(from, to) ||
    from.symbol.toLowerCase() === to.symbol.toLowerCase()
  ) {
    return "bridge";
  }
  return "swap-bridge";
}

function isDirectSwapType(pathType?: BridgePathType): boolean {
  return (
    pathType === "swap" ||
    pathType === "deposit-swap" ||
    pathType === "swap-withdraw"
  );
}

function isZeroAmount(value: string): boolean {
  return !value || value === "0" || Number(value) === 0;
}

function getQuoteFieldErrors(message: string): {
  from?: string;
  to?: string;
} {
  const normalized = message.trim().toLowerCase();
  if (!normalized) return {};

  if (normalized.startsWith("minimum ")) {
    return { from: message };
  }

  if (normalized.startsWith("available ") && normalized.includes("or more")) {
    return { to: message };
  }

  if (
    normalized.includes("insufficient balance") ||
    normalized.includes("not enough balance") ||
    normalized.includes("exceeds balance") ||
    normalized.includes("insufficient network fee")
  ) {
    return { from: message };
  }

  if (
    normalized.includes("insufficient liquidity") ||
    normalized.includes("not enough liquidity")
  ) {
    return { to: message };
  }

  return {};
}

function defaultSummary(flow: BridgeFlowState): BridgeTxSummary {
  const from = flow.fromToken;
  const to = flow.toToken;
  const pathType = inferPathType(from, to);
  const fromAmount = flow.fromAmount || "0";
  const toAmount = flow.toAmount || "0";
  const showBridge = pathType !== undefined && !isDirectSwapType(pathType);
  const showSwap = pathType !== undefined && pathType !== "bridge";

  return {
    pathType,
    bridgeInfo: showBridge ? [
      {
        label: "Route",
        value: from && to ? `${chainLabel(from.chainId)} → ${chainLabel(to.chainId)}` : "-",
      },
      {
        label: "Validator Fee",
        value: isZeroAmount(fromAmount) ? "0" : "Free",
        tone: isZeroAmount(fromAmount) ? "default" : "accent",
      },
      {
        label: "Network Fee",
        value: isZeroAmount(fromAmount) ? "0" : "Free",
        tone: isZeroAmount(fromAmount) ? "default" : "accent",
      },
    ] : undefined,
    swapInfo: showSwap ? [
      from && to
        ? {
            label: "Route",
            value: "",
            routeTokens: [
              { symbol: from.symbol, iconUrl: from.iconUrl },
              { symbol: to.symbol, iconUrl: to.iconUrl },
            ],
          }
        : { label: "Route", value: "-" },
      { label: "Price Impact", value: "0%" },
      {
        label: "In Amount",
        value: fromAmount,
        valueToken: from ? { symbol: from.symbol, iconUrl: from.iconUrl } : undefined,
      },
      {
        label: "Swap Fee",
        value: "0",
        valueToken: from ? { symbol: from.symbol, iconUrl: from.iconUrl } : undefined,
      },
      {
        label: "Out Amount",
        value: toAmount,
        valueToken: to ? { symbol: to.symbol, iconUrl: to.iconUrl } : undefined,
      },
    ] : undefined,
    txFee: [
      { label: "Priority Fee Per Gas", value: "0 Gwei" },
      { label: "Max Fee Per Gas", value: "0 Gwei" },
      { label: "Gas Limit", value: "0" },
    ],
    txFeeInfo: {
      estTxFee: "0",
      isDelegateFee: false,
    },
  };
}

function TokenIcon({ token, size = 20 }: { token?: BridgeToken; size?: number }) {
  const iconUrl = token?.iconUrl;
  const hasImage = Boolean(iconUrl);
  return (
    <span
      className="bf-token-icon"
      data-has-image={hasImage ? "true" : "false"}
      style={{ width: size, height: size }}
    >
      {iconUrl ? (
        <img src={iconUrl} alt="" width={size} height={size} draggable={false} />
      ) : (
        <span className="bf-token-fallback">{token?.symbol?.slice(0, 1) ?? "?"}</span>
      )}
    </span>
  );
}

function BridgeAmountPanel({
  label,
  token,
  amount,
  usd,
  onAmountChange,
  onTokenClick,
  locked = false,
}: {
  label: "From" | "To";
  token?: BridgeToken;
  amount: string;
  usd: string;
  onAmountChange: (value: string) => void;
  onTokenClick?: () => void;
  // When the side has no real choice (e.g. a single available "to" token), the
  // pill is fixed: hide the chevron and make it non-interactive.
  locked?: boolean;
}) {
  return (
    <section className="bf-amount-panel">
      <div className="bf-amount-head">
        <span>{label}</span>
        <span className="bf-balance">
          {formatBalance(token?.balance ?? "0")} {token?.symbol ?? ""}
        </span>
      </div>
      <div className="bf-amount-main">
        <label className="bf-amount-input-wrap">
          <span className="bf-sr-only">{label} amount</span>
          <input
            type="text"
            inputMode="decimal"
            value={amount}
            onChange={(event) => onAmountChange(event.target.value)}
            placeholder="0"
            className="bf-amount-input"
          />
          <span className="bf-amount-usd">{usd}</span>
        </label>
        <button
          type="button"
          className="bf-token-pill"
          onClick={locked ? undefined : onTokenClick}
          disabled={!locked && !onTokenClick}
          data-locked={locked ? "true" : undefined}
          data-track={locked ? undefined : "token_select_open"}
          data-track-id={label.toLowerCase()}
          aria-label={`Select ${label.toLowerCase()} token`}
        >
          <TokenIcon token={token} />
          <span>{token?.symbol ?? "Select"}</span>
          {!locked && <ChevronDownIcon size={16} />}
        </button>
      </div>
    </section>
  );
}

function BridgeTokenPicker({
  flow,
  side,
  onClose,
}: {
  flow: BridgeFlowState;
  side: TokenPickerSide;
  onClose: () => void;
}) {
  const titleId = React.useId();
  const [query, setQuery] = React.useState("");
  const [portalTarget, setPortalTarget] = React.useState<HTMLElement | null>(null);
  const theme = useThemeAttr();
  const selectedToken = side === "from" ? flow.fromToken : flow.toToken;

  React.useEffect(() => {
    setPortalTarget(document.body);
  }, []);

  React.useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") onClose();
    };
    document.addEventListener("keydown", handleKeyDown);
    return () => document.removeEventListener("keydown", handleKeyDown);
  }, [onClose]);

  const selectableTokens = React.useMemo(() => {
    if (side === "from") return flow.tokens;
    return flow.availableToTokens;
  }, [flow.availableToTokens, flow.tokens, side]);

  const filteredTokens = React.useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return selectableTokens;
    return selectableTokens.filter(
      (token) =>
        token.symbol.toLowerCase().includes(q) ||
        token.name.toLowerCase().includes(q),
    );
  }, [query, selectableTokens]);

  const handleSelect = React.useCallback(
    (token: BridgeToken) => {
      if (side === "from") {
        flow.selectFromToken(token);
      } else {
        flow.selectToToken(token);
      }
      onClose();
    },
    [flow, onClose, side],
  );

  if (!portalTarget) return null;

  return createPortal(
    <div
      className="bf-token-picker-overlay"
      data-theme={theme}
      role="presentation"
      onMouseDown={(event) => {
        if (event.target === event.currentTarget) onClose();
      }}
    >
      <section
        className="bf-token-picker"
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
      >
        <span className="bf-token-picker-handle" aria-hidden="true" />
        <header className="bf-token-picker-head">
          <h3 id={titleId}>Select a token</h3>
          <button
            type="button"
            className="bf-token-picker-close"
            onClick={onClose}
            aria-label="Close token selector"
          >
            <CloseIcon size={24} />
          </button>
        </header>

        <label className="bf-token-search">
          <SearchIcon size={20} />
          <span className="bf-sr-only">Search tokens</span>
          <input
            value={query}
            onChange={(event) => setQuery(event.target.value)}
            placeholder="Search Tokens"
            autoComplete="off"
            spellCheck={false}
          />
        </label>

        <div className="bf-token-list">
          {filteredTokens.map((token) => {
            const isSelected = sameBridgeAsset(token, selectedToken);
            const usd = isSelected
              ? formatTokenPriceUsd(token)
              : formatBalanceUsd(token);
            return (
              <button
                key={tokenKey(token)}
                type="button"
                className="bf-token-row"
                data-selected={isSelected ? "true" : "false"}
                onClick={() => handleSelect(token)}
              >
                <span className="bf-token-row-left">
                  <TokenIcon token={token} size={40} />
                  <span className="bf-token-meta">
                    <span className="bf-token-symbol">{token.symbol}</span>
                    {!isSelected && (
                      <span className="bf-token-name">{token.name}</span>
                    )}
                  </span>
                </span>
                <span className="bf-token-row-right">
                  <span className="bf-token-balance">
                    {formatBalance(token.balance)}
                    {!isSelected ? ` ${token.symbol}` : null}
                  </span>
                  {usd && <span className="bf-token-price">{usd}</span>}
                </span>
              </button>
            );
          })}
          {filteredTokens.length === 0 && (
            <div className="bf-token-empty">No tokens found</div>
          )}
        </div>
      </section>
    </div>,
    portalTarget,
  );
}

function BridgeSlippageSettings({
  flow,
  onClose,
}: {
  flow: BridgeFlowState;
  onClose: () => void;
}) {
  const titleId = React.useId();
  const [portalTarget, setPortalTarget] = React.useState<HTMLElement | null>(null);
  const selectedPreset = SLIPPAGE_PRESETS.find(
    (preset) => preset.value === flow.slippage,
  );
  const [customDraft, setCustomDraft] = React.useState(
    flow.slippage && !selectedPreset ? flow.slippage : "",
  );
  const hasCustomDraft = customDraft.trim() !== "" && customDraft.trim() !== ".";

  const customValue = hasCustomDraft ? Number(customDraft) : NaN;
  const exceedsMax = Number.isFinite(customValue) && customValue > 50;
  const highSlippage =
    Number.isFinite(customValue) && customValue > 5 && !exceedsMax;

  const selectPreset = (value: string) => {
    flow.setSlippage(value);
    onClose();
  };

  const applyCustom = () => {
    if (!hasCustomDraft || exceedsMax) return;
    flow.setSlippage(sanitizeSlippageInput(customDraft));
    onClose();
  };

  React.useEffect(() => {
    setPortalTarget(document.body);
  }, []);

  React.useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") onClose();
    };
    document.addEventListener("keydown", handleKeyDown);
    return () => document.removeEventListener("keydown", handleKeyDown);
  }, [onClose]);

  if (!portalTarget) return null;

  const theme = useThemeAttr();

  return createPortal(
    <div
      className="bf-slippage-overlay"
      data-theme={theme}
      role="presentation"
      onMouseDown={(event) => {
        if (event.target === event.currentTarget) onClose();
      }}
    >
      <section
        className="bf-slippage-dialog"
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
      >
        <header className="bf-slippage-head">
          <h3 id={titleId}>Slippage Settings</h3>
          <button
            type="button"
            className="bf-slippage-close"
            onClick={onClose}
            aria-label="Close slippage settings"
          >
            <CloseIcon size={24} />
          </button>
        </header>

        <div className="bf-slippage-body">
          <p className="bf-slippage-desc">
            The maximum price difference you are willing to accept before the
            transaction is reverted. Higher slippage increases the chance of
            execution but may result in a worse price.
          </p>

          <div className="bf-slippage-controls">
            <div className="bf-slippage-presets">
              {SLIPPAGE_PRESETS.map((preset) => (
                <button
                  key={preset.value}
                  type="button"
                  className="bf-slippage-preset"
                  data-selected={
                    !hasCustomDraft && flow.slippage === preset.value
                      ? "true"
                      : "false"
                  }
                  onClick={() => selectPreset(preset.value)}
                >
                  {preset.label}
                </button>
              ))}
            </div>

            <label
              className="bf-slippage-custom"
              data-selected={hasCustomDraft ? "true" : "false"}
            >
              <span className="bf-sr-only">Custom slippage percent</span>
              <input
                value={customDraft}
                onChange={(event) => {
                  setCustomDraft(sanitizeSlippageInput(event.target.value));
                }}
                onKeyDown={(event) => {
                  if (event.key === "Enter") {
                    event.preventDefault();
                    applyCustom();
                  }
                }}
                inputMode="decimal"
                placeholder="Enter custom slippage"
                autoComplete="off"
                spellCheck={false}
              />
              <span aria-hidden="true">%</span>
            </label>
          </div>

          {exceedsMax ? (
            <p className="bf-slippage-message" data-variant="error">
              Slippage cannot exceed 50%.
            </p>
          ) : highSlippage ? (
            <p className="bf-slippage-message" data-variant="warning">
              Your transaction may be frontrun due to high slippage.
            </p>
          ) : null}

          {hasCustomDraft && (
            <button
              type="button"
              className="bf-slippage-apply"
              onClick={applyCustom}
              disabled={exceedsMax}
            >
              Apply
            </button>
          )}
        </div>
      </section>
    </div>,
    portalTarget,
  );
}

function BridgeTermsModal({ flow }: { flow: BridgeFlowState }) {
  const titleId = React.useId();
  const [checked, setChecked] = React.useState(false);
  const [portalTarget, setPortalTarget] = React.useState<HTMLElement | null>(null);
  const theme = useThemeAttr();

  React.useEffect(() => {
    setPortalTarget(document.body);
  }, []);

  React.useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") flow.cancelTerms();
    };
    document.addEventListener("keydown", handleKeyDown);
    return () => document.removeEventListener("keydown", handleKeyDown);
  }, [flow]);

  if (!portalTarget || !flow.termsOpen) return null;

  return createPortal(
    <div
      className="bf-action-modal-overlay"
      data-theme={theme}
      role="presentation"
      onPointerDown={(event) => {
        if (event.target === event.currentTarget) flow.cancelTerms();
      }}
    >
      <section
        className="bf-action-modal bf-terms-modal"
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
      >
        <h3 id={titleId}>Agree to Terms of Service</h3>
        <p className="bf-action-modal-desc">
          Please make sure to review the Terms of Service before using CROSSD.
        </p>

        <div className="bf-terms-check-row" data-checked={checked ? "true" : "false"}>
          <button
            type="button"
            className="bf-terms-check-button"
            role="checkbox"
            aria-checked={checked}
            aria-label="Agree to Terms of Use"
            data-track="terms_check"
            onClick={() => setChecked((value) => !value)}
          >
            <span
              className="bf-terms-checkbox"
              data-checked={checked ? "true" : "false"}
              aria-hidden="true"
            />
          </button>
          <div className="bf-terms-copy">
            <span className="bf-terms-label">Agree to Terms of Use</span>
            {flow.termsUrl && (
              <a
                href={flow.termsUrl}
                target="_blank"
                rel="noreferrer"
              >
                Terms of Service
              </a>
            )}
          </div>
        </div>

        <p className="bf-terms-privacy">
          I have read and acknowledged the Privacy Policy to use CROSSD
        </p>

        <button
          type="button"
          className="bf-action-primary"
          disabled={!checked || flow.status === "submitting"}
          onClick={flow.confirmTerms}
          data-track="terms_confirm"
        >
          Confirm
        </button>
      </section>
    </div>,
    portalTarget,
  );
}

function CopyButton({ value, disabled = false }: { value: string; disabled?: boolean }) {
  const [copied, setCopied] = React.useState(false);
  return (
    <button
      type="button"
      className="bf-copy-button"
      aria-label="Copy token address"
      disabled={disabled}
      onClick={() => {
        if (disabled) return;
        void navigator.clipboard?.writeText(value).then(() => {
          setCopied(true);
          window.setTimeout(() => setCopied(false), 1200);
        });
      }}
    >
      {copied ? "OK" : "Copy"}
    </button>
  );
}

function BridgeApproveModal({
  flow,
  approval,
}: {
  flow: BridgeFlowState;
  approval: BridgeApprovalInfo;
}) {
  const titleId = React.useId();
  const [portalTarget, setPortalTarget] = React.useState<HTMLElement | null>(null);
  const theme = useThemeAttr();

  React.useEffect(() => {
    setPortalTarget(document.body);
  }, []);

  React.useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (flow.status === "submitting") return;
      if (event.key === "Escape") flow.cancelApproval();
    };
    document.addEventListener("keydown", handleKeyDown);
    return () => document.removeEventListener("keydown", handleKeyDown);
  }, [flow]);

  if (!portalTarget) return null;

  const disabled = flow.status === "submitting" || !flow.approveBridge;
  const isApproving = flow.status === "submitting";

  return createPortal(
    <div className="bf-action-modal-overlay" data-theme={theme} role="presentation">
      <section
        className="bf-action-modal bf-approve-modal"
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
      >
        <h3 id={titleId}>Approve {approval.token.symbol}</h3>
        <TokenIcon token={approval.token} size={72} />

        <div className="bf-approve-address-box">
          <span>Token Address</span>
          <div>
            <strong>{approval.tokenAddress}</strong>
            <CopyButton value={approval.tokenAddress} disabled={isApproving} />
          </div>
        </div>

        <div className="bf-action-buttons">
          <button
            type="button"
            className="bf-action-secondary"
            onClick={flow.cancelApproval}
            disabled={flow.status === "submitting"}
          >
            Cancel
          </button>
          <button
            type="button"
            className="bf-action-primary"
            onClick={flow.confirmApproval}
            disabled={disabled}
            aria-busy={isApproving}
            data-track="approve_confirm"
          >
            {isApproving && <span className="bf-action-button-spinner" aria-hidden="true" />}
            <span>{isApproving ? "Approving..." : "Approve"}</span>
          </button>
        </div>
      </section>
    </div>,
    portalTarget,
  );
}

function BridgeApprovalSuccessModal({ flow }: { flow: BridgeFlowState }) {
  const titleId = React.useId();
  const [portalTarget, setPortalTarget] = React.useState<HTMLElement | null>(null);
  const theme = useThemeAttr();

  React.useEffect(() => {
    setPortalTarget(document.body);
  }, []);

  React.useEffect(() => {
    if (!flow.approvalSuccessOpen) return;
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") flow.cancelApproval();
    };
    document.addEventListener("keydown", handleKeyDown);
    return () => document.removeEventListener("keydown", handleKeyDown);
  }, [flow]);

  if (!portalTarget || !flow.approvalSuccessOpen) return null;
  const isContinuing = flow.status === "submitting";

  return createPortal(
    <div className="bf-action-modal-overlay" data-theme={theme} role="presentation">
      <section
        className="bf-action-modal bf-approval-success-modal"
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
      >
        <h3 id={titleId}>Approval Success</h3>
        <span className="bf-success-mark" aria-hidden="true" />
        <p className="bf-action-modal-desc">
          Token approval confirmed successfully.
          <br />
          Please continue to the next step.
        </p>

        <div className="bf-action-buttons">
          <button
            type="button"
            className="bf-action-secondary"
            onClick={flow.cancelApproval}
            disabled={isContinuing}
          >
            Cancel
          </button>
          <button
            type="button"
            className="bf-action-primary"
            onClick={flow.continueAfterApproval}
            disabled={isContinuing}
            aria-busy={isContinuing}
          >
            {isContinuing && <span className="bf-action-button-spinner" aria-hidden="true" />}
            <span>{isContinuing ? "Continuing..." : "Continue"}</span>
          </button>
        </div>
      </section>
    </div>,
    portalTarget,
  );
}

function ExchangeRate({
  fromAmount,
  toAmount,
  fromToken,
  toToken,
}: {
  fromAmount: string;
  toAmount: string;
  fromToken?: BridgeToken;
  toToken?: BridgeToken;
}) {
  const [isRateReversed, setIsRateReversed] = React.useState(false);

  const exchangeRate = React.useMemo(() => {
    const from = new BigNumber(fromAmount || "0");
    const to = new BigNumber(toAmount || "0");
    if (!from.isFinite() || !to.isFinite() || from.lte(0) || to.lte(0)) {
      return null;
    }
    return isRateReversed ? from.dividedBy(to) : to.dividedBy(from);
  }, [fromAmount, isRateReversed, toAmount]);

  if (!exchangeRate || !fromToken || !toToken) return null;

  // Same token across different chains (e.g. CROSS on BNB <-> CROSS on Cross)
  // always bridges 1:1, so the "1 CROSS = 1 CROSS" rate adds no information.
  if (fromToken.symbol === toToken.symbol) return null;

  const fromSymbol = isRateReversed ? toToken.symbol : fromToken.symbol;
  const toSymbol = isRateReversed ? fromToken.symbol : toToken.symbol;

  return (
    <button
      type="button"
      className="bf-exchange-rate"
      onClick={() => setIsRateReversed((value) => !value)}
      data-track="rate_reverse"
    >
      <span>1 {fromSymbol}</span>
      <ArrowRightLeftIcon size={12} />
      <span>{formatRateDecimal(exchangeRate)} {toSymbol}</span>
    </button>
  );
}

function BridgeStatusToast({ flow }: { flow: BridgeFlowState }) {
  if (!flow.submitToast) return null;
  return (
    <div
      className="bf-status-toast"
      data-variant="processing"
      role="status"
      aria-live="polite"
    >
      <span className="bf-status-toast-icon" aria-hidden="true">
        <span className="bf-status-spinner" />
      </span>
      <span className="bf-status-toast-copy">
        <strong>Transaction Processing</strong>
        <span>Your transaction is being processed on the blockchain</span>
      </span>
    </div>
  );
}

function BridgeErrorToast({ message }: { message: string }) {
  const [visible, setVisible] = React.useState(false);

  React.useEffect(() => {
    if (!message) {
      setVisible(false);
      return undefined;
    }

    setVisible(true);
    const timer = window.setTimeout(() => {
      setVisible(false);
    }, 3500);

    return () => window.clearTimeout(timer);
  }, [message]);

  if (!message || !visible) return null;

  return (
    <div
      className="bf-status-toast"
      data-variant="error"
      role="alert"
      aria-live="assertive"
    >
      <span className="bf-status-toast-icon" aria-hidden="true">
        <span className="bf-status-error-mark">!</span>
      </span>
      <span className="bf-status-toast-copy">
        <strong>Error</strong>
        <span>{message}</span>
      </span>
    </div>
  );
}

function successTitle(pathType?: BridgePathType): string {
  switch (pathType) {
    case "swap":
    case "deposit-swap":
    case "swap-withdraw":
      return "Swap Transaction Sent";
    case "bridge":
      return "Bridge Transaction Sent";
    case "swap-bridge":
      return "Swap & Bridge Transaction Sent";
    case "bridge-swap":
      return "Bridge & Swap Transaction Sent";
    default:
      return "Transaction Sent";
  }
}

function BridgeSuccessModal({ flow }: { flow: BridgeFlowState }) {
  const titleId = React.useId();
  const [portalTarget, setPortalTarget] = React.useState<HTMLElement | null>(null);
  const submitted = flow.submitted;
  const theme = useThemeAttr();

  React.useEffect(() => {
    setPortalTarget(document.body);
  }, []);

  React.useEffect(() => {
    if (!flow.successDialogOpen) return;
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") flow.dismissSuccessDialog();
    };
    document.addEventListener("keydown", handleKeyDown);
    return () => document.removeEventListener("keydown", handleKeyDown);
  }, [flow]);

  if (!portalTarget || !flow.successDialogOpen || !submitted) return null;

  return createPortal(
    <div className="bf-success-overlay" data-theme={theme} role="presentation">
      <section
        className="bf-success-modal"
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
      >
        <div className="bf-success-body">
          <span className="bf-success-mark" aria-hidden="true" />
          <div className="bf-success-copy">
            <strong id={titleId}>{successTitle(submitted.pathType)}</strong>
            <span>Please check the results in the history.</span>
          </div>

          <div className="bf-success-route" aria-hidden="true">
            {(submitted.routeTokens && submitted.routeTokens.length > 0
              ? submitted.routeTokens
              : [
                  { symbol: submitted.fromToken.symbol, iconUrl: submitted.fromToken.iconUrl },
                  { symbol: submitted.toToken.symbol, iconUrl: submitted.toToken.iconUrl },
                ]
            ).map((token, index) => (
              <React.Fragment key={`${token.symbol}-${index}`}>
                {index > 0 && <span className="bf-success-route-arrow">→</span>}
                <InfoTokenGlyph token={token} size={43} />
              </React.Fragment>
            ))}
          </div>

          <button
            type="button"
            className="bf-success-ok"
            onClick={flow.dismissSuccessDialog}
            data-track="success_ok"
          >
            OK
          </button>
        </div>
      </section>
    </div>,
    portalTarget,
  );
}

function InfoTokenGlyph({
  token,
  size = 16,
}: {
  token: BridgeInfoTokenRef;
  size?: number;
}) {
  const hasImage = Boolean(token.iconUrl);
  return (
    <span
      className="bf-token-icon"
      data-has-image={hasImage ? "true" : "false"}
      style={{ width: size, height: size }}
    >
      {token.iconUrl ? (
        <img src={token.iconUrl} alt="" width={size} height={size} draggable={false} />
      ) : (
        <span className="bf-token-fallback">{token.symbol?.slice(0, 1) ?? "?"}</span>
      )}
    </span>
  );
}

function InfoRowValue({ row }: { row: BridgeInfoRow }) {
  if (row.routeTokens && row.routeTokens.length > 0) {
    return (
      <span className="bf-info-route">
        {row.routeTokens.map((token, index) => (
          <React.Fragment key={`${token.symbol}-${index}`}>
            {index > 0 && (
              <span className="bf-info-route-arrow" aria-hidden="true">
                →
              </span>
            )}
            <InfoTokenGlyph token={token} />
          </React.Fragment>
        ))}
      </span>
    );
  }
  if (row.valueToken) {
    return (
      <span className="bf-info-value-token">
        <InfoTokenGlyph token={row.valueToken} />
        <span>{row.value}</span>
      </span>
    );
  }
  return <>{row.value}</>;
}

function InfoRows({ rows }: { rows?: BridgeInfoRow[] }) {
  if (!rows || rows.length === 0) return null;
  return (
    <div className="bf-info-rows">
      {rows.map((row, index) => (
        <div className="bf-info-row" key={`${row.label}-${row.value}`}>
          <span className="bf-info-label">
            <span className="bf-tree-marker">{index === rows.length - 1 ? "└" : "├"}</span>
            <span>{row.label}</span>
          </span>
          <span className="bf-info-value" data-tone={row.tone ?? "default"}>
            <InfoRowValue row={row} />
          </span>
        </div>
      ))}
    </div>
  );
}

function getInfoTitle(
  summary: BridgeTxSummary,
  hasBridgeInfo: boolean,
  hasSwapInfo: boolean,
): string {
  if (summary.pathType === "bridge") return "Bridge Info";
  if (isDirectSwapType(summary.pathType)) return "Swap Info";
  if (hasBridgeInfo && hasSwapInfo) return "Swap & Bridge Info";
  if (hasBridgeInfo) return "Bridge Info";
  return "Swap Info";
}

function OrderedInfoSections({
  summary,
}: {
  summary: BridgeTxSummary;
}) {
  const hasBridgeInfo = Boolean(summary.bridgeInfo?.length);
  const hasSwapInfo = Boolean(summary.swapInfo?.length);
  const showNestedTitles = hasBridgeInfo && hasSwapInfo;
  const sections =
    summary.pathType === "bridge-swap"
      ? (["bridge", "swap"] as const)
      : (["swap", "bridge"] as const);

  return (
    <>
      {sections.map((section) => {
        const rows = section === "swap" ? summary.swapInfo : summary.bridgeInfo;
        if (!rows?.length) return null;

        return (
          <div className="bf-info-section" key={section}>
            {showNestedTitles && (
              <h4>{section === "swap" ? "Swap Info" : "Bridge Info"}</h4>
            )}
            <InfoRows rows={rows} />
          </div>
        );
      })}
    </>
  );
}

function BridgeDetails({ summary }: { summary: BridgeTxSummary }) {
  const hasBridgeInfo = Boolean(summary.bridgeInfo?.length);
  const hasSwapInfo = Boolean(summary.swapInfo?.length);
  const hasTxFee = Boolean(summary.txFee?.length);
  const infoTitle = getInfoTitle(summary, hasBridgeInfo, hasSwapInfo);
  const [infoOpen, setInfoOpen] = React.useState(true);
  const [txFeeOpen, setTxFeeOpen] = React.useState(true);

  return (
    <div className="bf-details">
      {summary.liquidity && (
        <section className="bf-liquidity">
          <div className="bf-liquidity-head">
            <span>{summary.liquidity.label ?? "Available Liquidity"}</span>
            <span className="bf-liquidity-status" data-status={summary.liquidity.status}>
              {summary.liquidity.status ?? "Normal"}
            </span>
          </div>
          <div className="bf-liquidity-track">
            <span style={{ width: `${summary.liquidity.percentage ?? 50}%` }} />
          </div>
        </section>
      )}
      {(hasBridgeInfo || hasSwapInfo) && (
        <section
          className="bf-detail-section bf-info-card"
          data-open={infoOpen ? "true" : "false"}
        >
          <button
            type="button"
            className="bf-collapse-head"
            aria-expanded={infoOpen}
            onClick={() => setInfoOpen((open) => !open)}
            data-track="details_toggle"
          >
            <h3>{infoTitle}</h3>
            <ChevronDownIcon size={16} className="bf-collapse-chevron" />
          </button>
          {infoOpen && (
            <div className="bf-collapse-body">
              <OrderedInfoSections summary={summary} />
            </div>
          )}
        </section>
      )}
      {hasTxFee && (
        <section
          className="bf-detail-section bf-tx-fee-section"
          data-open={txFeeOpen ? "true" : "false"}
        >
          <button
            type="button"
            className="bf-collapse-head bf-tx-fee-head"
            aria-expanded={txFeeOpen}
            onClick={() => setTxFeeOpen((open) => !open)}
            data-track="details_toggle"
          >
            <h3>Est. Tx Fee</h3>
            <div className="bf-tx-fee-total">
              {summary.txFeeInfo?.isDelegateFee && (
                <span className="bf-fee-delegated">Fee Delegated</span>
              )}
              {summary.txFeeInfo?.tokenIconUrl && (
                <img
                  className="bf-tx-fee-token"
                  src={summary.txFeeInfo.tokenIconUrl}
                  alt={summary.txFeeInfo.tokenSymbol ?? ""}
                  width={16}
                  height={16}
                  draggable={false}
                />
              )}
              <span
                data-delegated={
                  summary.txFeeInfo?.isDelegateFee &&
                  Number(summary.txFeeInfo?.estTxFee) > 0
                    ? "true"
                    : undefined
                }
              >
                {clampDecimals(summary.txFeeInfo?.estTxFee ?? "0")}
              </span>
              <ChevronDownIcon size={16} className="bf-collapse-chevron" />
            </div>
          </button>
          {txFeeOpen && (
            <div className="bf-collapse-body">
              <InfoRows rows={summary.txFee} />
            </div>
          )}
        </section>
      )}
    </div>
  );
}

function HistoryCard({ item }: { item: BridgeHistoryItem }) {
  const status = getHistoryStatus(item);
  const fiatAmount = formatHistoryUsd(item.fromAmount, item.fromToken);
  return (
    <article className="bf-history-item">
      <div className="bf-history-item-head">
        <span className="bf-history-status" data-status={status}>
          {historyStatusLabel(status)}
        </span>
        <time className="bf-history-time" dateTime={item.timestamp}>
          {formatHistoryTimestamp(item.timestamp)}
        </time>
      </div>
      <div className="bf-history-item-body">
        <strong className="bf-history-amount">
          {formatHistoryTokenAmount(item.fromAmount)} {item.fromToken.symbol}
        </strong>
        <div className="bf-history-meta">
          <span className="bf-history-usd">{fiatAmount ?? shortHash(item.txHash)}</span>
          <span className="bf-history-route">
            <span className="bf-history-route-token">
              <TokenIcon token={item.fromToken} size={16} />
              <span>{item.fromToken.symbol}</span>
            </span>
            <ArrowRightIcon size={16} className="bf-history-route-arrow" />
            <span className="bf-history-route-token">
              <TokenIcon token={item.toToken} size={16} />
              <span>{item.toToken.symbol}</span>
            </span>
          </span>
        </div>
      </div>
    </article>
  );
}

function HistoryView({ flow }: { flow: BridgeFlowState }) {
  const [filter, setFilter] = React.useState<HistoryFilter>("all");
  const hasPending = flow.history.some((item) => getHistoryStatus(item) === "pending");
  const visibleHistory = flow.history.filter((item) => historyMatchesFilter(item, filter));
  const emptyMessage =
    filter === "all"
      ? "No bridge history yet."
      : `No ${filter} bridge history.`;

  return (
    <main className="bf-history-body">
      <nav className="bf-history-tabs" aria-label="Bridge history filters">
        {HISTORY_FILTERS.map((item) => (
          <button
            key={item.value}
            type="button"
            className="bf-history-tab"
            data-active={filter === item.value ? "true" : "false"}
            onClick={() => setFilter(item.value)}
            data-track="history_filter"
            data-track-id={item.value}
          >
            <span>{item.label}</span>
            {item.value === "pending" && hasPending && (
              <span className="bf-history-tab-dot" aria-hidden="true" />
            )}
          </button>
        ))}
      </nav>
      {visibleHistory.length > 0 ? (
        <div className="bf-history-list">
          {visibleHistory.map((item) => (
            <HistoryCard key={item.id || item.txHash} item={item} />
          ))}
        </div>
      ) : (
        <div className="bf-empty">{emptyMessage}</div>
      )}
    </main>
  );
}

function FormView({
  flow,
  env,
}: {
  flow: BridgeFlowState;
  env: Environment;
}) {
  const [tokenPickerSide, setTokenPickerSide] =
    React.useState<TokenPickerSide | null>(null);
  const [slippageSettingsOpen, setSlippageSettingsOpen] = React.useState(false);
  const [swapRotation, setSwapRotation] = React.useState(0);
  const hasAmount = Boolean(flow.fromAmount.trim() || flow.toAmount.trim());
  const summary = hasAmount ? flow.summary ?? defaultSummary(flow) : null;
  // apps.json(bridgePoweredBy.url)이 단일 소스. 로드 전에는 링크 미표시.
  const poweredByUrl = useRemoteConfigValue<AppsConfig, string>(
    "apps.json",
    env,
    (config) => resolveEnvValue(config?.bridgePoweredBy?.url, env),
  );
  const fieldErrors = getQuoteFieldErrors(flow.quoteError);
  const insufficientBalance = exceedsBalance(flow.fromAmount, flow.fromToken);
  const fromError = insufficientBalance ? "Insufficient balance" : fieldErrors.from;
  const commonQuoteError =
    flow.quoteError && !fieldErrors.from && !fieldErrors.to
      ? flow.quoteError
      : "";
  const toastError = flow.error || commonQuoteError;
  // Show the dot whenever history has a pending tx (persists across reloads),
  // or a freshly-submitted tx is awaiting its first view.
  const hasPendingHistory = flow.history.some(
    (item) => getHistoryStatus(item) === "pending",
  );
  // Slippage only applies to swap-bearing routes. A pure cross-chain bridge of
  // the same asset (e.g. CROSS↔CROSS, USDT↔USDT) has no swap, so hide it there.
  const slippagePathType =
    summary?.pathType ?? inferPathType(flow.fromToken, flow.toToken);
  const showSlippage = slippagePathType !== "bridge";

  return (
    <>
      <main className="bf-body">
        <div className="bf-toolbar">
          <button
            type="button"
            className="bf-chip bf-chip-history"
            onClick={() => flow.setStep("history")}
            data-track="history_open"
          >
            <HistoryIcon />
            <span>History</span>
            {(hasPendingHistory || flow.hasNewHistory) && (
              <span className="bf-chip-dot" aria-hidden="true" />
            )}
          </button>
          {showSlippage && (
            <button
              type="button"
              className="bf-chip"
              onClick={() => setSlippageSettingsOpen(true)}
              data-track="slippage_open"
              disabled={flow.slippageLocked}
              aria-haspopup="dialog"
              aria-expanded={slippageSettingsOpen}
            >
              <SlidersIcon />
              <span>
                {flow.slippageLocked
                  ? "Auto 1%"
                  : formatSlippageLabel(flow.slippage, flow.effectiveSlippage)}
              </span>
            </button>
          )}
        </div>

        <div
          className="bf-swap-card"
          data-has-from-error={fromError ? "true" : "false"}
        >
          <BridgeAmountPanel
            label="From"
            token={flow.fromToken}
            amount={flow.fromAmount}
            usd={formatUsd(flow.fromAmount, flow.fromToken)}
            onAmountChange={(value) => flow.setAmount(value, "from")}
            onTokenClick={() => setTokenPickerSide("from")}
          />
          {fromError && (
            <div className="bf-field-error">{fromError}</div>
          )}
          <button
            type="button"
            className="bf-swap-toggle"
            onClick={() => { flow.swapTokens(); setSwapRotation((r) => r + 360); }}
            data-track="token_swap"
            style={{ transform: `translateX(-50%) rotate(${swapRotation}deg)`, transition: "transform 0.2s ease" }}
          >
            <ArrowDownIcon size={22} />
          </button>
          <BridgeAmountPanel
            label="To"
            token={flow.toToken}
            amount={flow.toAmount}
            usd={formatUsd(flow.toAmount, flow.toToken)}
            onAmountChange={(value) => flow.setAmount(value, "to")}
            onTokenClick={() => setTokenPickerSide("to")}
            locked={flow.availableToTokens.length <= 1}
          />
          {fieldErrors.to && (
            <div className="bf-field-error">{fieldErrors.to}</div>
          )}
        </div>

        <ExchangeRate
          fromAmount={flow.fromAmount}
          toAmount={flow.toAmount}
          fromToken={flow.fromToken}
          toToken={flow.toToken}
        />

        {summary && (
          <BridgeDetails summary={summary} />
        )}
      </main>
      <footer className="bf-footer">
        <button
          type="button"
          className="bf-next"
          onClick={flow.submit}
          data-track="next"
          disabled={flow.status === "submitting" || Boolean(flow.quoteError) || insufficientBalance}
        >
          {flow.status === "submitting" ? "Processing..." : "Next"}
        </button>
        {poweredByUrl && (
          <a
            className="bf-powered"
            href={poweredByUrl}
            target="_blank"
            rel="noreferrer"
          >
            Powered by <span className="bf-powered-brand">CROSS DeFi</span>
          </a>
        )}
      </footer>
      {tokenPickerSide && (
        <BridgeTokenPicker
          flow={flow}
          side={tokenPickerSide}
          onClose={() => setTokenPickerSide(null)}
        />
      )}
      {slippageSettingsOpen && (
        <BridgeSlippageSettings
          flow={flow}
          onClose={() => setSlippageSettingsOpen(false)}
        />
      )}
      <BridgeTermsModal flow={flow} />
      {flow.approvalInfo && (
        <BridgeApproveModal flow={flow} approval={flow.approvalInfo} />
      )}
      <BridgeApprovalSuccessModal flow={flow} />
      <BridgeStatusToast flow={flow} />
      <BridgeErrorToast message={flow.submitToast ? "" : toastError} />
      <BridgeSuccessModal flow={flow} />
    </>
  );
}

export function BridgeFlow({
  onClose,
  onBackToWallet,
  env,
  className,
  ...rest
}: BridgeFlowProps) {
  const resolvedEnv = React.useMemo(() => resolveEnvironment(env), [env]);
  const flow = useBridgeFlow({
    ...rest,
    onExit: onBackToWallet,
  });

  const rootRef = React.useRef<HTMLDivElement>(null);
  const theme = useDetectTheme(rootRef);
  const title = flow.step === "history" ? "History" : "Swap & Bridge";

  return (
    <TrackingBoundary feature="bridge">
    <ThemeContext.Provider value={theme}>
    <div
      ref={rootRef}
      className={cn("bf-root", className)}
      data-view={flow.step}
      data-status={flow.status}
    >
      <header className="bf-header">
        <button type="button" className="bf-header-btn" onClick={flow.back} aria-label="Back">
          <BackIcon size={24} />
        </button>
        <span className="bf-header-title">{title}</span>
        <button type="button" className="bf-header-btn" onClick={onClose} aria-label="Close">
          <CloseIcon size={24} />
        </button>
      </header>

      {flow.step === "history" ? (
        <HistoryView flow={flow} />
      ) : (
        <FormView flow={flow} env={resolvedEnv} />
      )}
    </div>
    </ThemeContext.Provider>
    </TrackingBoundary>
  );
}
